/**
 * The DRAFT-AUTOSAVE seam (IoC) — in-progress, unsaved seamless-input text
 * (a `Begründung`, an annotation comment/rewrite, a ruling decision) survives a
 * page reload even though no review EVENT has been appended yet.
 *
 * This is a client-only companion to the {@link RlhfSessionStore}: the session
 * store is the durable, append-only episodic log (persisted server-side, the
 * source of truth once an action is SAVED); the draft store is the volatile
 * pre-save buffer, kept in the browser's `localStorage`, keyed per
 * (session, field). A field's draft is written debounced as the operator types
 * and CLEARED the moment that field's event lands in the session store — so a
 * saved value is never shadowed by a stale draft.
 *
 * Like the session store it is provided by the host via the
 * {@link RlhfDraftStoreToken} context token and injected OPTIONALLY: an element
 * with no draft-store provider simply keeps no drafts (see {@link optionalDraftStore}).
 * All timing/storage is injectable so the pure logic is testable without a DOM.
 */

import { createToken, type ContextToken } from "@platform/web-kit"

/** What the grading elements need to autosave a seamless field's pre-save text. */
export interface RlhfDraftStore {
  /** The most-recent draft for a field (pending or persisted), or null if none. */
  read(field: string): string | null
  /** Buffer a field's draft (debounced persist). An empty string clears the draft. */
  write(field: string, value: string): void
  /** Drop a field's draft — called once its event is saved into the session store. */
  clear(field: string): void
}

/** The IoC token the host provides the draft store under (optional per consumer). */
export const RlhfDraftStoreToken = createToken<RlhfDraftStore>("rlhf-draft-store")

/** The minimal Web-Storage surface the draft store needs (localStorage-shaped). */
export interface DraftStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/** A debounce scheduler seam — returns a cancel handle. Injectable for tests. */
export interface DraftScheduler {
  set(fn: () => void, ms: number): () => void
}

const defaultScheduler: DraftScheduler = {
  set(fn, ms) {
    const id = setTimeout(fn, ms)
    return () => clearTimeout(id)
  },
}

/**
 * Resolve `localStorage` where present, else a no-op store (SSR / a locked-down
 * browser where storage access throws) — drafts are a best-effort nicety, never
 * a hard dependency, so an unavailable storage degrades to "keeps nothing".
 */
function resolveStorage(): DraftStorage {
  try {
    const ls = (globalThis as { localStorage?: DraftStorage }).localStorage
    if (ls) return ls
  } catch {
    // access denied (private mode / policy) — fall through to the no-op
  }
  return {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  }
}

export interface LocalDraftStoreOptions {
  /** Namespaces every key so two sessions in one browser never collide. */
  sessionId: string
  /** Default: `localStorage` (or a no-op where unavailable). */
  storage?: DraftStorage
  /** Debounce window before a write hits storage. Default 400ms. */
  debounceMs?: number
  /** Default: `setTimeout`/`clearTimeout`. */
  scheduler?: DraftScheduler
}

export interface LocalDraftStore extends RlhfDraftStore {
  /** Persist every pending draft immediately (e.g. on `visibilitychange`/unload). */
  flush(): void
  /** Cancel all pending timers (element/surface teardown). Drafts already written stay. */
  dispose(): void
}

/** The namespaced storage key for one (session, field) draft. Pure. */
export function draftKey(sessionId: string, field: string): string {
  return `rlhf-draft:${sessionId}:${field}`
}

/**
 * A `localStorage`-backed {@link RlhfDraftStore}. Writes are debounced per field
 * (the last value within the window wins); reads return the pending value if not
 * yet flushed, else the persisted one; clear cancels any pending write and removes
 * the key. Empty/whitespace-only writes clear rather than persist an empty draft.
 */
export function createLocalDraftStore(options: LocalDraftStoreOptions): LocalDraftStore {
  const { sessionId, debounceMs = 400 } = options
  const storage = options.storage ?? resolveStorage()
  const scheduler = options.scheduler ?? defaultScheduler
  const pending = new Map<string, { value: string; cancel: () => void }>()

  const persist = (field: string, value: string): void => {
    storage.setItem(draftKey(sessionId, field), value)
  }

  const drop = (field: string): void => {
    const p = pending.get(field)
    if (p) {
      p.cancel()
      pending.delete(field)
    }
  }

  return {
    read(field) {
      const p = pending.get(field)
      if (p) return p.value
      return storage.getItem(draftKey(sessionId, field))
    },
    write(field, value) {
      // An emptied field is a clear, not an empty draft to restore later.
      if (value.trim().length === 0) {
        drop(field)
        storage.removeItem(draftKey(sessionId, field))
        return
      }
      drop(field)
      const cancel = scheduler.set(() => {
        pending.delete(field)
        persist(field, value)
      }, debounceMs)
      pending.set(field, { value, cancel })
    },
    clear(field) {
      drop(field)
      storage.removeItem(draftKey(sessionId, field))
    },
    flush() {
      for (const [field, { value }] of [...pending]) {
        pending.get(field)?.cancel()
        pending.delete(field)
        persist(field, value)
      }
    },
    dispose() {
      for (const { cancel } of pending.values()) cancel()
      pending.clear()
    },
  }
}

/**
 * Resolve the optional draft store from context — returns null when no ancestor
 * provides it (the store seam is REQUIRED; the draft seam is a nicety). Structural
 * `ctx` type so this stays decoupled from KitContext's full surface.
 */
export function optionalDraftStore(ctx: { inject<T>(token: ContextToken<T>): T }): RlhfDraftStore | null {
  try {
    return ctx.inject(RlhfDraftStoreToken)
  } catch {
    return null
  }
}
